import type { FeedItemKind } from "@scrile/contracts";

/**
 * «Печатает…» между пузырями, как в мессенджере.
 *
 * Два источника:
 * 1. Запланированный пузырь. Реплики сцены записаны заранее с паузами (deliver_at), и
 *    лента подсказывает, через сколько станет виден следующий (x-feed-next-in). Перед
 *    ним — окно «печатает»: не раньше прихода подсказки и не дольше TYPING_LEAD_MS; пузыри
 *    вплотную друг к другу (окно короче TYPING_MIN_MS) не мигают индикатором. После срока
 *    окно держится ещё TYPING_GRACE_MS — лента приносит пузырь с задержкой опроса, а
 *    исчезнувшее и вернувшееся «печатает» выглядит сбоем.
 * 2. Ответ на сообщение. Подписчик написал — ответ героя пишет модель, и срока его никто
 *    не знает, пока он не записан. Через REPLY_READ_MS («прочитал») — «печатает», пока не
 *    придёт что-нибудь от героя, но не дольше REPLY_TYPING_MAX_MS: герой может и промолчать.
 *
 * Функция чистая: хук зовёт её по таймеру в recheckAt.
 */

export const TYPING_LEAD_MS = 4_000;
/** Голосовое «записывают» дольше, чем печатают строку. */
export const RECORDING_LEAD_MS = 6_000;
export const TYPING_MIN_MS = 700;
export const TYPING_GRACE_MS = 8_000;
export const REPLY_READ_MS = 1_200;
export const REPLY_TYPING_MAX_MS = 25_000;

export type TypingKind = "typing" | "recording" | "sending_photo" | "sending_video";

export interface TypingInput {
  now: number;
  /** Когда станет виден следующий элемент (часы клиента: приход ответа + x-feed-next-in). */
  nextAt: number | null;
  nextKind: FeedItemKind | null;
  /** Когда пришла подсказка: раньше этого «печатает» не начинается (мы ещё не знали). */
  hintAt: number | null;
  /** Подписчик отправил сообщение, ответа героя ещё нет: с какого момента ждём. */
  awaitingSince: number | null;
}

export interface TypingState {
  kind: TypingKind | null;
  /** Когда пересчитать: ближайшая граница окна после now; null — ничего не ждём. */
  recheckAt: number | null;
}

interface Window {
  start: number;
  end: number;
  kind: TypingKind;
}

function kindOf(next: FeedItemKind | null): TypingKind | null {
  switch (next) {
    case "text":
    case "hero_card":
      return "typing";
    case "voice":
      return "recording";
    case "photo":
      return "sending_photo";
    case "video":
      return "sending_video";
    // Выход серии, пейвол, служебное — это не человек пишет
    default:
      return null;
  }
}

/** Окно перед запланированным пузырём; null — не показывать. */
export function scheduledWindow(input: Pick<TypingInput, "nextAt" | "nextKind" | "hintAt">): Window | null {
  const { nextAt, nextKind, hintAt } = input;
  if (nextAt === null) return null;
  const kind = kindOf(nextKind);
  if (!kind) return null;
  const lead = kind === "recording" ? RECORDING_LEAD_MS : TYPING_LEAD_MS;
  const start = Math.max(hintAt ?? Number.NEGATIVE_INFINITY, nextAt - lead);
  if (nextAt - start < TYPING_MIN_MS) return null;
  return { start, end: nextAt + TYPING_GRACE_MS, kind };
}

export function replyWindow(awaitingSince: number | null): Window | null {
  if (awaitingSince === null) return null;
  return { start: awaitingSince + REPLY_READ_MS, end: awaitingSince + REPLY_TYPING_MAX_MS, kind: "typing" };
}

export function typingState(input: TypingInput): TypingState {
  const windows = [scheduledWindow(input), replyWindow(input.awaitingSince)].filter((w): w is Window => w !== null);
  let kind: TypingKind | null = null;
  let recheckAt: number | null = null;
  for (const window of windows) {
    if (input.now >= window.start && input.now < window.end) {
      // Запланированный пузырь точнее догадки: его вид (голосовое, фото) важнее
      kind ??= window.kind;
    }
    for (const edge of [window.start, window.end]) {
      if (edge > input.now) recheckAt = recheckAt === null ? edge : Math.min(recheckAt, edge);
    }
  }
  return { kind, recheckAt };
}
